import React, { useState } from 'react';
import { 
  Download, FileSpreadsheet, FileText, File, Calendar,
  CheckCircle, Loader2, AlertCircle, Award
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';

const ExportCenter = () => {
  const { shouldShowData } = useDemoData();

  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [exporting, setExporting] = useState(null);
  const [exportStatus, setExportStatus] = useState({});
  const { token } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const exportFormats = [
    {
      id: 'excel',
      name: 'Excel (.xlsx)',
      description: 'Full cost data with summary and details sheets. Best for analysis.',
      icon: FileSpreadsheet,
      color: 'text-green-500 bg-green-100 dark:bg-green-900/30',
      endpoint: '/api/export/costs/excel'
    },
    {
      id: 'csv',
      name: 'CSV',
      description: 'Raw cost data for import into other tools like Tableau or Power BI.',
      icon: File,
      color: 'text-emerald-500 bg-emerald-100 dark:bg-blue-900/30',
      endpoint: '/api/export/costs/csv'
    },
    {
      id: 'pdf',
      name: 'PDF Report',
      description: 'Executive summary report for board presentations and stakeholders.',
      icon: FileText,
      color: 'text-red-500 bg-red-100 dark:bg-red-900/30',
      endpoint: '/api/export/report/pdf'
    },
    {
      id: 'focus',
      name: 'FOCUS 1.1 (Industry Standard)',
      description: 'FinOps Open Cost & Usage Specification — interoperable with every FinOps tool.',
      icon: Award,
      color: 'text-emerald-600 bg-emerald-100 dark:bg-emerald-900/30',
      endpoint: '/api/exports/focus',
      isStandard: true,
      ignoreDates: true,
    }
  ];

  const handleExport = async (format) => {
    setExporting(format.id);
    setExportStatus({ ...exportStatus, [format.id]: 'loading' });

    try {
      const url = format.ignoreDates
        ? `${API_URL}${format.endpoint}?days=30`
        : `${API_URL}${format.endpoint}?start_date=${startDate}&end_date=${endDate}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `cartie_export_${startDate}_to_${endDate}`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      } else {
        if (format.id === 'excel') filename += '.xlsx';
        else if (format.id === 'csv') filename += '.csv';
        else if (format.id === 'pdf') filename += '.pdf';
      }

      // Download the file
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      setExportStatus({ ...exportStatus, [format.id]: 'success' });
      setTimeout(() => {
        setExportStatus({ ...exportStatus, [format.id]: null });
      }, 3000);
    } catch (error) {
      console.error('Export error:', error);
      setExportStatus({ ...exportStatus, [format.id]: 'error' });
    } finally {
      setExporting(null);
    }
  };

  const getStatusIcon = (formatId) => {
    const status = exportStatus[formatId];
    if (exporting === formatId) return <Loader2 className="w-5 h-5 animate-spin" />;
    if (status === 'success') return <CheckCircle className="w-5 h-5 text-green-500" />;
    if (status === 'error') return <AlertCircle className="w-5 h-5 text-red-500" />;
    return <Download className="w-5 h-5" />;
  };

  if (!shouldShowData) {
    return <EmptyDataState title="Export Center" description="Connect your cloud account to see real data here." />;
  }


  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="export-center-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <Download className="w-8 h-8 text-emerald-500" />
          Export Center
        </h1>
        <p className="text-slate-400 dark:text-gray-400 mt-1">
          Export your cost data in various formats for analysis and reporting
        </p>
      </div>

      {/* Date Range Selector */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
          <Calendar className="w-5 h-5 text-emerald-500" />
          Select Date Range
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              data-testid="start-date-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
              data-testid="end-date-input"
            />
          </div>
        </div>
        
        {/* Quick Date Presets */}
        <div className="flex flex-wrap gap-2 mt-4">
          <button
            onClick={() => {
              const now = new Date();
              const start = new Date(now.getFullYear(), now.getMonth(), 1);
              setStartDate(start.toISOString().split('T')[0]);
              setEndDate(now.toISOString().split('T')[0]);
            }}
            className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            This Month
          </button>
          <button
            onClick={() => {
              const now = new Date();
              const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
              const end = new Date(now.getFullYear(), now.getMonth(), 0);
              setStartDate(start.toISOString().split('T')[0]);
              setEndDate(end.toISOString().split('T')[0]);
            }}
            className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Last Month
          </button>
          <button
            onClick={() => {
              const now = new Date();
              const quarterMonth = Math.floor(now.getMonth() / 3) * 3;
              const start = new Date(now.getFullYear(), quarterMonth, 1);
              setStartDate(start.toISOString().split('T')[0]);
              setEndDate(now.toISOString().split('T')[0]);
            }}
            className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            This Quarter
          </button>
          <button
            onClick={() => {
              const now = new Date();
              const start = new Date(now.getFullYear(), 0, 1);
              setStartDate(start.toISOString().split('T')[0]);
              setEndDate(now.toISOString().split('T')[0]);
            }}
            className="px-3 py-1 text-sm bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Year to Date
          </button>
        </div>
      </div>

      {/* Export Formats */}
      <div className="space-y-4">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white">Choose Export Format</h2>
        
        {exportFormats.map((format) => {
          const Icon = format.icon;
          return (
            <div
              key={format.id}
              className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg flex items-center justify-between hover:shadow-xl transition-shadow"
              data-testid={`export-format-${format.id}`}
            >
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${format.color}`}>
                  <Icon className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
                    {format.name}
                    {format.isStandard && (
                      <span className="text-[11px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30">
                        FOCUS 1.1
                      </span>
                    )}
                  </h3>
                  <p className="text-slate-400 dark:text-gray-400 text-sm">{format.description}</p>
                </div>
              </div>
              <button
                onClick={() => handleExport(format)}
                disabled={exporting !== null}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                  exporting === format.id
                    ? 'bg-gray-100 dark:bg-gray-700 text-slate-400 cursor-wait'
                    : exportStatus[format.id] === 'success'
                    ? 'bg-green-500 text-white'
                    : exportStatus[format.id] === 'error'
                    ? 'bg-red-500 text-white'
                    : 'bg-emerald-500 text-white hover:bg-blue-600'
                }`}
                data-testid={`export-btn-${format.id}`}
              >
                {getStatusIcon(format.id)}
                {exporting === format.id ? 'Exporting...' : 
                 exportStatus[format.id] === 'success' ? 'Downloaded!' :
                 exportStatus[format.id] === 'error' ? 'Failed' : 'Export'}
              </button>
            </div>
          );
        })}
      </div>

      {/* Tips */}
      <div className="bg-emerald-50 dark:bg-blue-900/20 rounded-xl p-6">
        <h3 className="font-bold text-emerald-700 dark:text-blue-400 mb-2">Export Tips</h3>
        <ul className="text-sm text-emerald-600 dark:text-blue-300 space-y-1">
          <li>• Excel format includes summary sheets with charts and detailed cost breakdown</li>
          <li>• CSV is best for importing into BI tools like Tableau, Power BI, or Looker</li>
          <li>• PDF reports are formatted for executive presentations and board meetings</li>
          <li>• Large date ranges may take longer to export</li>
        </ul>
      </div>
    </div>
  );
};

export default ExportCenter;
